import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ArrowRight, HandCoins, TriangleAlert, Wallet } from 'lucide-react';
import { useConfirmedMutation } from '../../lib/confirm';
import { Button, Modal, InputNumber, Textarea, Spinner, message } from '../../ui';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { inr2 } from '../../lib/format';
import { ExpenseClaimReview } from './ExpenseClaimReview';

/** The API's refusal code, whichever envelope it came in. */
export const errorCode = (e: any): string | undefined =>
  e?.response?.data?.error?.code ?? e?.response?.data?.code ?? e?.response?.data?.error?.details?.code;
export const errorText = (e: any, fallback = 'That did not go through'): string =>
  e?.response?.data?.error?.message ?? e?.response?.data?.message ?? e?.message ?? fallback;

const num = (v: any) => Number(v ?? 0);

/**
 * APPROVE AN EXPENSE CLAIM — settlement V2, one dialog everywhere a claim is approved.
 *
 * The approver decides ONE thing: how much of the claim the company accepts. The money is then settled by
 * the rule, not by a question — wallet first, then collection cash, and whatever is left is reimbursed —
 * and the figures come from the SERVER's preview, the same calculation the approval commits. Approve sends
 * the three figures it showed back with the request; if the rep's wallet or collection moved in between,
 * the server refuses (MONEY_POSITION_CHANGED) and this dialog refreshes rather than settling numbers nobody
 * saw.
 */
export function SettlementApproveDialog({ reportId, onClose, onDone }: { reportId: string; onClose: () => void; onDone?: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | null>(null);
  const [debounced, setDebounced] = useState<number | null>(null);
  const [remarks, setRemarks] = useState('');
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [changed, setChanged] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState('');

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['expense-report', reportId],
    queryFn: () => request<any>({ url: `/expense-reports/${reportId}` }),
    refetchOnMount: 'always',
  });
  const claimed = num(report?.subtotalAmount);
  // DEF-060 · the person who filed this claim on the rep's behalf may not decide it (server: FILED_ON_BEHALF).
  // Approve is already held back by the preview's blocker, whose sentence says so; Send back is not offered either.
  const myId = useAuthStore((s) => s.user?.id);
  const filedForRep = !!report?.createdBy && String(report.createdBy) === String(myId) && String(report.createdBy) !== String(report.repId);

  // Pre-filled with the claimed total — the approver confirms an amount, never fills in a blank.
  useEffect(() => {
    if (report && amount == null) { setAmount(claimed); setDebounced(claimed); }
  }, [report]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(amount), 350);
    return () => clearTimeout(t);
  }, [amount]);

  const { data: preview, isFetching: previewLoading, isError: previewFailed, refetch: refetchPreview } = useQuery({
    queryKey: ['expense-settlement-preview', reportId, debounced],
    queryFn: () => request<any>({
      url: `/expense-reports/${reportId}/settlement-preview`,
      params: debounced != null ? { approvedAmount: String(debounced) } : undefined,
    }),
    enabled: !!report && debounced != null,
    refetchOnMount: 'always',
  });

  const repName = report?.repName ?? 'the rep';
  const approved = num(preview?.approved ?? amount);
  const less = amount != null && amount < claimed - 0.005;
  const proofMissing: any[] = preview?.proofMissing ?? [];
  const blockers: Array<{ code: string; message: string }> = preview?.blockers ?? [];
  const reimbursement = num(preview?.reimbursementDue);
  const stale = debounced !== amount || previewLoading;

  const problems = useMemo(() => {
    const out: string[] = [];
    if (preview?.amountError) out.push(preview.amountError);
    if (less && !remarks.trim()) out.push('Say why you are approving less than claimed.');
    if (proofMissing.length && !preview?.canOverrideProof) out.push('Required proof is missing — send it back for the proof.');
    if (proofMissing.length && preview?.canOverrideProof && (!override || !overrideReason.trim())) {
      out.push('Required proof is missing. Send it back, or approve without it as Super Admin and give the reason.');
    }
    return out;
  }, [preview, less, remarks, proofMissing.length, override, overrideReason]);

  const canApprove = !!preview && !stale && !previewFailed && !blockers.length && !problems.length && report?.status === 'SUBMITTED';

  const done = (text: string) => {
    message.success(text);
    qc.invalidateQueries({ queryKey: ['expense-report', reportId] });
    qc.invalidateQueries({ queryKey: ['expense-reports'] });
    qc.invalidateQueries({ queryKey: ['approvals'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
    onDone?.();
    onClose();
  };

  const approve = useConfirmedMutation(
    {
      title: `Approve ${inr2(approved)}?`,
      content: reimbursement > 0
        ? `${inr2(preview?.walletUsed)} comes off ${repName}'s wallet and ${inr2(preview?.collectionUsed)} off their collection cash. ${inr2(reimbursement)} goes to Accounts to reimburse.`
        : `${inr2(preview?.walletUsed)} comes off ${repName}'s wallet and ${inr2(preview?.collectionUsed)} off their collection cash. Nothing is left to reimburse, so the claim closes.`,
      okText: 'Yes, approve',
    },
    {
      mutationFn: () => request<any>({
        url: `/expense-reports/${reportId}/approve`, method: 'PATCH',
        data: {
          approvedAmount: amount,
          ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
          ...(proofMissing.length && override ? { proofOverrideReason: overrideReason.trim() } : {}),
          expected: { walletUsed: preview?.walletUsed, collectionUsed: preview?.collectionUsed, reimbursementDue: preview?.reimbursementDue },
        },
      }),
      onSuccess: (res: any) => done(res?.settled
        ? `${report?.reportNo} approved and closed — nothing to reimburse`
        : `${report?.reportNo} approved — ${inr2(res?.settlement?.reimbursementDue ?? reimbursement)} sent to Accounts to reimburse`),
      onError: (e: any) => {
        if (errorCode(e) === 'MONEY_POSITION_CHANGED' || /balance changed/i.test(errorText(e))) {
          setChanged(true);
          refetchPreview();
          message.warning('Wallet or collection balance changed. Review the updated settlement before approving.');
        } else {
          message.error(errorText(e));
        }
      },
    },
  );

  const sendBack = useMutation({
    mutationFn: () => request({ url: `/expense-reports/${reportId}/send-back`, method: 'PATCH', data: { reason: sendBackReason.trim() } }),
    onSuccess: () => done(`${report?.reportNo} sent back to ${repName}`),
    onError: (e: any) => message.error(errorText(e)),
  });

  const walletBefore = num(preview?.walletBefore);
  const collectionBefore = num(preview?.collectionBefore);
  const awaiting = num(preview?.collectionAwaitingBankConfirmation);

  return (
    <Modal
      open
      width={1100}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={report ? `Approve · ${report.reportNo}` : 'Approve claim'}
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          {report && (
            <Link to={`/expense-reports/${reportId}`} className="mr-auto inline-flex items-center gap-1 type-body-medium text-brand-700 hover:underline dark:text-brand-300">
              Open the full claim <ArrowRight size={14} />
            </Link>
          )}
          {report?.status === 'SUBMITTED' && !filedForRep && (
            <Button variant={proofMissing.length ? 'soft-danger' : 'default'} onClick={() => {
              setSendBackReason(proofMissing.length
                ? `Attach the missing proof and submit again: ${proofMissing.map((p) => `${p.what} (${String(p.label).toLowerCase()})`).join(', ')}.`
                : '');
              setSendBackOpen(true);
            }}>
              {proofMissing.length ? 'Send back for proof' : 'Send back'}
            </Button>
          )}
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={approve.isPending} disabled={!canApprove} onClick={() => approve.mutate()}>
            {`Approve ${inr2(approved)}`}{reimbursement > 0 ? ` · ${inr2(reimbursement)} to reimburse` : ''}
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading the claim…</div>
      ) : isError || !report ? (
        <div className="rounded-card border border-danger-500/40 bg-danger-500/10 px-3 py-2.5 text-sm text-danger-600 dark:text-danger-500">
          Couldn’t load this claim. Close this and try again.
        </div>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
          {/* LEFT — the claim and the company money the rep is holding right now. */}
          <div className="min-w-0 space-y-3">
            <ExpenseClaimReview report={report} mode="approve" hideMoney canOverrideProof={!!preview?.canOverrideProof} />
            <section className="rounded-card border border-line bg-surface px-3 py-2.5 text-sm">
              <h4 className="mb-2 type-overline text-subtle">Company money with {repName} now</h4>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-control border border-line/70 bg-surface-2 px-3 py-2">
                  <div className="flex items-center gap-1.5 type-label text-muted"><Wallet size={14} aria-hidden /> Wallet (company float)</div>
                  <div className="mt-0.5 type-body-strong tabular-nums text-content">{preview ? inr2(walletBefore) : '…'}</div>
                </div>
                <div className="rounded-control border border-line/70 bg-surface-2 px-3 py-2">
                  <div className="flex items-center gap-1.5 type-label text-muted"><HandCoins size={14} aria-hidden /> Collection (retailer cash to deposit)</div>
                  <div className="mt-0.5 type-body-strong tabular-nums text-content">{preview ? inr2(collectionBefore) : '…'}</div>
                  {awaiting > 0 && (
                    <div className="mt-0.5 type-caption text-muted">+ {inr2(awaiting)} banked, waiting for bank confirmation — can’t be used</div>
                  )}
                </div>
              </div>
            </section>
          </div>

          {/* RIGHT — the decision, and exactly what it does to the money. */}
          <div className="min-w-0 space-y-3 rounded-card border border-brand-500/25 bg-brand-500/5 px-3.5 py-3">
            {changed && (
              <div className="flex items-start gap-2 rounded-card border border-warning-500/40 bg-warning-500/10 px-3 py-2 text-sm text-warning-600 dark:text-harvest-300">
                <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
                <div>Wallet or collection balance changed while this was open. The figures below are the updated settlement — check them before approving.</div>
              </div>
            )}
            {!!blockers.length && (
              <div className="space-y-1 rounded-card border border-danger-500/40 bg-danger-500/10 px-3 py-2 text-sm text-danger-600 dark:text-danger-500">
                {blockers.map((b) => <div key={b.code}>{b.message}</div>)}
              </div>
            )}

            {/* ① HOW MUCH */}
            <section>
              <h4 className="mb-1.5 type-overline text-subtle">① How much to approve</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between gap-3"><span className="text-muted">Claimed</span><span className="tabular-nums text-content">{inr2(claimed)}</span></div>
                <InputNumber className="w-full" min={0} prefix="₹" value={amount} onChange={(v) => setAmount(v == null ? null : Number(v))} />
                <div className="flex justify-between gap-3">
                  <span className="text-muted">Not approved</span>
                  <span className={`tabular-nums ${less ? 'text-danger-600 dark:text-danger-500' : 'text-content'}`}>{inr2(Math.max(0, claimed - (amount ?? claimed)))}</span>
                </div>
                {preview?.amountError && <p className="type-caption text-danger-600 dark:text-danger-500">{preview.amountError}</p>}
              </div>
            </section>

            {/* ② HOW IT SETTLES */}
            <section>
              <h4 className="mb-1.5 type-overline text-subtle">② How it will be settled <span className="normal-case">(automatic: wallet first, then collection)</span></h4>
              {!preview ? (
                <div className="flex items-center gap-2 py-3 text-sm text-muted"><Spinner /> Working out the settlement…</div>
              ) : (
                <div className={`overflow-x-auto rounded-control border border-line bg-surface ${stale ? 'opacity-60' : ''}`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line text-left type-caption text-subtle">
                        <th className="px-2.5 py-1.5 font-medium" />
                        <th className="px-2.5 py-1.5 text-right font-medium">Before</th>
                        <th className="px-2.5 py-1.5 text-right font-medium">Used</th>
                        <th className="px-2.5 py-1.5 text-right font-medium">After</th>
                      </tr>
                    </thead>
                    <tbody className="tabular-nums">
                      <tr className="border-b border-line/60">
                        <td className="px-2.5 py-1.5 text-content">Wallet</td>
                        <td className="px-2.5 py-1.5 text-right text-muted">{inr2(preview.walletBefore)}</td>
                        <td className="px-2.5 py-1.5 text-right text-content">{inr2(preview.walletUsed)}</td>
                        <td className="px-2.5 py-1.5 text-right text-content">{inr2(preview.walletAfter)}</td>
                      </tr>
                      <tr className="border-b border-line/60">
                        <td className="px-2.5 py-1.5 text-content">Collection</td>
                        <td className="px-2.5 py-1.5 text-right text-muted">{inr2(preview.collectionBefore)}</td>
                        <td className="px-2.5 py-1.5 text-right text-content">{inr2(preview.collectionUsed)}</td>
                        <td className="px-2.5 py-1.5 text-right text-content">{inr2(preview.collectionAfter)}</td>
                      </tr>
                      <tr className="border-b border-line/60">
                        <td className="px-2.5 py-1.5 text-content">Company pays {repName}</td>
                        <td className="px-2.5 py-1.5" />
                        <td className={`px-2.5 py-1.5 text-right font-medium ${reimbursement > 0 ? 'text-warning-600 dark:text-harvest-300' : 'text-content'}`}>{inr2(preview.reimbursementDue)}</td>
                        <td className="px-2.5 py-1.5 text-right type-caption text-subtle">{reimbursement > 0 ? 'reimbursement' : ''}</td>
                      </tr>
                      <tr>
                        <td className="px-2.5 py-1.5 font-medium text-content">Total</td>
                        <td className="px-2.5 py-1.5" />
                        <td className="px-2.5 py-1.5 text-right font-semibold text-content">{inr2(preview.approved)}</td>
                        <td className="px-2.5 py-1.5 text-right type-caption text-success-600 dark:text-success-500">= approved ✓</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* ③ WHAT HAPPENS */}
            {preview && (
              <section>
                <h4 className="mb-1.5 type-overline text-subtle">③ What happens</h4>
                <ul className="list-disc space-y-0.5 pl-5 text-sm text-content">
                  <li>{num(preview.walletUsed) > 0 ? `${inr2(preview.walletUsed)} taken from ${repName}'s wallet` : `Nothing taken from ${repName}'s wallet`}</li>
                  <li>{num(preview.collectionUsed) > 0
                    ? `${inr2(preview.collectionUsed)} taken from collection — ${inr2(preview.collectionAfter)} of collection cash left with them to deposit`
                    : `Nothing taken from collection — ${inr2(preview.collectionBefore)} of collection cash still with them to deposit`}</li>
                  <li>{reimbursement > 0
                    ? `${inr2(reimbursement)} reimbursement goes to Accounts to pay; the claim stays “Approved” until it is paid`
                    : 'Nothing to reimburse — the claim closes on approval'}</li>
                </ul>
              </section>
            )}

            {/* PROOF OVERRIDE — Super Admin only, explicit, with a reason. */}
            {!!proofMissing.length && preview?.canOverrideProof && (
              <section className="rounded-card border border-danger-500/40 bg-danger-500/5 px-3 py-2">
                <label className="flex items-start gap-2 text-sm text-content">
                  <input type="checkbox" className="mt-1" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                  <span>Approve without the missing proof (Super Admin) — {proofMissing.map((p) => `${p.what}: ${String(p.label).toLowerCase()}`).join(', ')}</span>
                </label>
                {override && (
                  <Textarea className="mt-2" rows={2} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Why is this being approved without the proof? (recorded in the audit trail)" />
                )}
              </section>
            )}

            <section>
              <label className="mb-1 block type-label text-content">
                {less ? <>Why less than claimed? <span className="text-danger-600">*</span></> : <>Remarks <span className="text-subtle">(optional)</span></>}
              </label>
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                placeholder={less ? 'The reason the rep is being paid less than they claimed' : 'Anything worth recording with this decision'} />
            </section>

            {!!problems.length && !blockers.length && (
              <ul className="space-y-0.5 text-sm text-danger-600 dark:text-danger-500">
                {problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}
            {previewFailed && <p className="text-sm text-danger-600 dark:text-danger-500">Couldn’t work out the settlement. Close this and try again.</p>}
          </div>
        </div>
      )}

      <Modal
        open={sendBackOpen}
        onOpenChange={(o) => { if (!o) setSendBackOpen(false); }}
        title={`Send ${report?.reportNo ?? 'claim'} back to ${repName}`}
        footer={
          <>
            <Button variant="default" onClick={() => setSendBackOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={!sendBackReason.trim()} loading={sendBack.isPending} onClick={() => sendBack.mutate()}>Send back</Button>
          </>
        }
      >
        <Textarea rows={3} value={sendBackReason} onChange={(e) => setSendBackReason(e.target.value)} placeholder="What needs fixing (the rep sees this)" />
      </Modal>
    </Modal>
  );
}
